import axios from "axios"

export const getPublicIP = async () => {
    try {
        const response = await axios.get("https://httpbin.org/ip")
        return response.data.origin
    } catch {
        return "Unavailable"
    }
}

const PRIVATE_IPV4_RANGES = [
    ['0.0.0.0', 8], ['10.0.0.0', 8], ['100.64.0.0', 10], ['127.0.0.0', 8], ['169.254.0.0', 16],
    ['172.16.0.0', 12], ['192.168.0.0', 16], ['198.18.0.0', 15], ['224.0.0.0', 4], ['240.0.0.0', 4],
]
const IPV4_MAPPED_PREFIX = '::ffff:'

const parseIpv4 = (address) => {
    const parts = address.split('.')
    if (parts.length !== 4 || !parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255)) return null
    return parts.reduce((acc, part) => acc * 256 + Number(part), 0)
}

const ipv4InRange = (value, [network, prefix]) => {
    const size = 2 ** (32 - prefix)
    return Math.floor(value / size) === Math.floor(parseIpv4(network) / size)
}

const expandEmbeddedIpv4 = (groups) => {
    const last = groups[groups.length - 1]
    if (!last || !last.includes('.')) return groups
    const v4 = parseIpv4(last)
    if (v4 === null) return null
    return [...groups.slice(0, -1), Math.floor(v4 / 65536).toString(16), (v4 % 65536).toString(16)]
}

const parseIpv6 = (address) => {
    const halves = address.split('::')
    if (halves.length > 2) return null
    const compressed = halves.length === 2
    const toGroups = (text) => (text ? text.split(':') : [])
    const head = compressed ? toGroups(halves[0]) : expandEmbeddedIpv4(toGroups(halves[0]))
    const tail = compressed ? expandEmbeddedIpv4(toGroups(halves[1])) : []
    if (!head || !tail) return null
    if (!head.concat(tail).every((group) => /^[0-9a-f]{1,4}$/i.test(group))) return null
    const missing = 8 - head.length - tail.length
    if (compressed ? missing < 1 : missing !== 0) return null
    return [...head, ...Array(missing).fill('0'), ...tail].map((group) => parseInt(group, 16))
}

const unmapIpv4 = (address) => (address.toLowerCase().startsWith(IPV4_MAPPED_PREFIX)
    && address.includes('.') ? address.slice(IPV4_MAPPED_PREFIX.length) : address)

export const isIpAddress = (address) => {
    if (typeof address !== 'string') return false
    const text = unmapIpv4(address.trim())
    return parseIpv4(text) !== null || parseIpv6(text) !== null
}

export const isPrivateIp = (address) => {
    if (typeof address !== 'string') return false
    const text = unmapIpv4(address.trim())
    const v4 = parseIpv4(text)
    if (v4 !== null) return PRIVATE_IPV4_RANGES.some((range) => ipv4InRange(v4, range))
    const groups = parseIpv6(text)
    if (!groups) return false
    const first = groups[0]
    const allZeroBeforeLast = groups.slice(0, 7).every((group) => group === 0)
    return (allZeroBeforeLast && groups[7] <= 1)
        || (first & 0xfe00) === 0xfc00
        || (first & 0xffc0) === 0xfe80
        || (first & 0xff00) === 0xff00
}

export const signerIpLabel = (signer) => {
    const address = signer?.ipAddress
    if (signer?.ipSource) {
        if (signer.ipPrivate || signer.ipSource === 'socket') return 'Not available (proxy)'
        return address || 'Not recorded'
    }
    return isIpAddress(address) && !isPrivateIp(address) ? `${address} (reported by browser)` : 'Not recorded'
}

export const uploadedSignDocumentLabel = (doc, uploadedTypeId) => {
    if (!uploadedTypeId || String(doc?.type?._id ?? doc?.type) !== String(uploadedTypeId)) return null
    if (doc?.status !== 'signed' || (doc?.signers || []).length > 0) return null
    const name = [doc?.creator?.firstName, doc?.creator?.lastName].filter(Boolean).join(' ')
    return `Uploaded by ${name || 'N/A'}`
}
